import React from 'react';
import { Navigate } from 'react-router';
import { useReactiveVar } from '@apollo/client';
import { currentUserVar } from '@/graphql/operations/user';
import { useLocation } from 'react-router-dom';
import { getAccessToken } from '@/utils/accessToken';
import { ResourceUIType } from '@/service/Api/types';
import { isTokenExpired } from '@/utils/tokenValidation';

type Props = React.PropsWithChildren<any>;

export default function RequireAuth({ children }: Props) {
  const accessToken = getAccessToken();
  const currentUser = useReactiveVar(currentUserVar);
  const location = useLocation();
  const layoutAccess = currentUser.permissions.layoutAccess;

  const disableWorkerDashboard = currentUser?.organization?.config?.disableWorkerDashboard;

  const hasCompliancePermission = layoutAccess?.find((p) => p.resource === ResourceUIType.Compliance);
  const hasDashboardPermission = layoutAccess?.find((p) => p.resource === ResourceUIType.Dashboard);
  const hasSchedulesPermission = layoutAccess?.find((p) => p.resource === ResourceUIType.Schedules);
  const hasAvailabilityPermission = layoutAccess?.find((p) => p.resource === ResourceUIType.Availability);

  const onlyCompliancePermission = hasCompliancePermission && (!hasDashboardPermission || !hasSchedulesPermission);
  const onlyAvailabilityPermission = hasAvailabilityPermission && disableWorkerDashboard;

  if (!accessToken || isTokenExpired(accessToken)) {
    // Redirect them to the /login page, but save the current location they were
    // trying to go to when they were redirected. This allows us to send them
    // along to that page after they login, which is a nicer user experience
    // than dropping them off on the home page.
    return <Navigate to="/signin" replace />;
  } else {
    if (location.pathname === '/') {
      if (currentUser.permissions?.isAdmin) {
        if (currentUser.organization.config.enableAdminDashboard) {
          return <Navigate to="/admin-dashboard" replace />;
        } else {
          return <Navigate to="/earnings" replace />;
        }
      } else {
        let defaultRedirectRoute =
          currentUser.permissions?.layoutAccess?.find((p) => p?.resource === ResourceUIType.Dashboard)?.resource ||
          'my-pay';
        if (onlyCompliancePermission) {
          defaultRedirectRoute = 'employee-compliance-agent';
        } else if (onlyAvailabilityPermission) {
          defaultRedirectRoute = 'employee-preferences-agent';
        }
        return <Navigate to={`/${defaultRedirectRoute}`} replace />;
      }
    } else {
      return <>{children}</>;
    }
  }
}
